import React, { useCallback, useMemo, useState } from "react";
import WHOTabs from "../../../../controls/WHOTabs";
import { TabModel } from "../../../../../models/TabModel";
import {
  TabWithTablesChartsModel,
  MultipleChildTabsModel,
} from "../../../../../models/TabWithTablesChartsModel";
import TablesWithCharts from "../../../../common/TablesWithCharts";

type MultipleTabsProps = {
  data: Array<MultipleChildTabsModel>;
  orientation: "vertical" | "horizontal" | undefined;
  noRecordsText?: string;
};

/** Data analysis DL DQA component which renders nested tabs for the indicators with (table/graph components) */
const MultipleTabsReport = (props: MultipleTabsProps) => {
  const { data, orientation, noRecordsText } = props;
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [currentSubTab, setCurrentSubTab] = useState<number>(0);

  const onTabChange = useCallback((_event: React.ChangeEvent<{}>, tabIndex: number) => {
    setCurrentTab(tabIndex);
    setCurrentSubTab(0);
  }, []);

  const onSubTabChange = useCallback((_event: React.ChangeEvent<{}>, tabIndex: number) => {
    setCurrentSubTab(tabIndex);
  }, []);

  // Tab label models only — no children JSX embedded, avoids building all tabs' heavy trees
  const tabLabelModels = useMemo<Array<TabModel>>(
    () => data?.map((tab: MultipleChildTabsModel) => new TabModel(tab.tabId, tab.tabName, <></>)) ?? [],
    [data]
  );

  // Sub-tab label models for the currently active outer tab only
  const subTabLabelModels = useMemo<Array<TabModel>>(
    () =>
      data?.[currentTab]?.tabs?.map((tab: TabWithTablesChartsModel) =>
        new TabModel(tab.tabId, tab.tabName, <></>)
      ) ?? [],
    [data, currentTab]
  );

  // Only the active sub-tab's content is computed and rendered
  const activeContent = useMemo(() => {
    const activeSubTab = data?.[currentTab]?.tabs?.[currentSubTab];
    if (!activeSubTab) return null;
    return (
      <TablesWithCharts
        tables={activeSubTab.tables}
        charts={activeSubTab.charts}
        noRecordsText={noRecordsText}
      />
    );
  }, [data, currentTab, currentSubTab]);

  if (!data?.length) return <></>;

  return (
    <div className='app-tab-wrapper'>
      <WHOTabs
        tabs={tabLabelModels}
        value={currentTab}
        onChange={onTabChange}
        scrollable={false}
      >
        <div className='p-2'>
          <WHOTabs
            tabs={subTabLabelModels}
            value={currentSubTab}
            onChange={onSubTabChange}
            scrollable={false}
            orientation={orientation}
          >
            <div className='p-2'>
              {activeContent}
            </div>
          </WHOTabs>
        </div>
      </WHOTabs>
    </div>
  );
};

export default MultipleTabsReport;
